import {
	Button,
	CardBody,
	CardFooter,
	Drawer,
	DrawerHeader,
	DrawerHeading,
	FileInputField,
	Icon,
	SubmitButton,
	UploadFile,
	isSuccess,
	useOpen,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { clippingsService } from "../../shared/api/clippingsService";

export const FileUploadDrawer: FC = () => {
	const { clippingId = "" } = useParams();
	const { handleOpen, open, handleClose } = useOpen();
	const { showToast } = useToasts();
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const createFile = useCallback(
		async (values: UploadFile) => {
			if (values.file) {
				const response = await clippingsService.uploadFile(clippingId, values);
				if (isSuccess(response)) {
					showToast({
						body: t("File was saved"),
						title: t("File"),
						type: "success",
					});
					await queryClient.invalidateQueries({ queryKey: clippingsService.showKey(clippingId) });
				} else {
					showToast({
						body: response.statusText,
						title: t("Could not save file"),
						type: "error",
					});
				}
			}
			handleClose();
		},
		[handleClose, clippingId, showToast, t, queryClient],
	);

	return (
		<>
			<Button
				title={t("File upload")}
				prefix={<Icon $icon="file_upload" title={t("File upload")} />}
				onClick={handleOpen}
				size="small"
			>
				{t("File upload")}
			</Button>
			<Drawer open={open} onClosed={handleClose}>
				<DrawerHeader onClose={handleClose}>
					<DrawerHeading>{t("File upload")}</DrawerHeading>
				</DrawerHeader>
				<Formik initialValues={{ file: null, isPublic: false }} onSubmit={createFile}>
					<Form>
						<CardBody>
							<FileInputField label={t("File")} name="file" />
						</CardBody>
						<CardFooter>
							<SubmitButton title={t("Save")} name={t("Save")}>
								{t("Save")}
							</SubmitButton>
						</CardFooter>
					</Form>
				</Formik>
			</Drawer>
		</>
	);
};
